import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as authService from '../services/auth'
import type { LoginRequest, VerifyOtpRequest, AdminUser } from '../types'

// Query keys for React Query caching
export const authKeys = {
  all: ['auth'] as const,
  user: () => [...authKeys.all, 'user'] as const
}

// Login hook
export function useLogin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (credentials: LoginRequest) => authService.login(credentials),
    onSuccess: (response) => {
      if (response.success) {
        console.log("Login successful", response)
        queryClient.invalidateQueries({ queryKey: authKeys.all })
      }
    },
    onError: (error) => {
      console.error('Login failed:', error)
    }
  })
}

// OTP verification hook
export function useVerifyOtp() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (credentials: VerifyOtpRequest) => authService.verifyOtp(credentials),
    onSuccess: (response) => {
      if (response.success) {
        console.log("OTP verification successful", response)
        queryClient.invalidateQueries({ queryKey: authKeys.all })
      }
    },
    onError: (error) => {
      console.error('OTP verification failed:', error)
    }
  })
}

// Logout hook
export function useLogout() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => authService.logout(),
    onSuccess: () => {
      queryClient.clear()
    },
    onError: (error) => {
      console.error('Logout failed:', error)
      queryClient.clear()
    }
  })
}

// Get current user hook
export function useCurrentUser() {
  return useQuery({
    queryKey: authKeys.user(),
    queryFn: (): AdminUser => {
      const user = authService.getCurrentUser()
      if (!user) {
        throw new Error('No authenticated user')
      }
      return user
    },
    enabled: authService.isAuthenticated(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false
  })
}

// Main auth hook with all auth state
export function useAuth() {
  const { data: user, isLoading, error } = useQuery({
    queryKey: authKeys.user(),
    queryFn: (): AdminUser => {
      const user = authService.getCurrentUser()
      const isAuth = authService.isAuthenticated()

      if (!isAuth || !user) {
        throw new Error('No authenticated user')
      }
      return user
    },
    retry: false,
    staleTime: 0, // Always check fresh
    refetchOnMount: true,
    refetchOnWindowFocus: true
  })

  return {
    user,
    isAuthenticated: authService.isAuthenticated(),
    isLoading,
    error,
    hasRole: (role: string) => authService.hasRole(role)
  }
}